"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4 (portalul e pe React 19, deci doar clasele s-au schimbat).
// Vezi components/wingo/README.md.

// Wingo UI Pro · https://wingo-ui.com/legal/license

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useInsertionEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ComponentProps,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Popover as PopoverPrimitive } from "radix-ui";
import {
  AnimatePresence,
  animate,
  motion,
  useDragControls,
  useMotionValue,
  useTransform,
  type MotionValue,
  type PanInfo,
} from "motion/react";
import { ArrowRight, Check, X } from "lucide-react";
import { Button } from "@/components/wingo/ui/button";
import { Kbd } from "@/components/wingo/ui/kbd";
import { Progress } from "@/components/wingo/ui/progress";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";
import { useKeyboardInset } from "@/components/wingo/hooks/use-keyboard-inset";
import { useIsMobile, useIsTouch } from "@/components/wingo/hooks/use-media-query";
import { usePersistedState } from "@/components/wingo/hooks/use-persisted-state";
import { isTypingTarget, useShortcut } from "@/components/wingo/hooks/use-shortcut";
import { haptic } from "@/components/wingo/lib/haptics";
import { gestures, shouldComplete, springs, useMotionPreset, useMotionTransition, usePrefersReducedMotion } from "@/components/wingo/lib/motion";
import { colorVars, toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";

// ---------------------------------------------------------------- types

export type TourSide = "top" | "right" | "bottom" | "left" | "auto";
export type TourAlign = "start" | "center" | "end";
export type TourTarget = string | (() => Element | null) | RefObject<Element | null>;
export type TourProgressStyle = "dots" | "fraction" | "bar" | "none";
export type TourSize = "sm" | "md";
export type TourRadius = "none" | "sm" | "md" | "lg" | "full";
export type TourMobileLayout = "docked" | "popover";
export type TourScrollBehavior = "smooth" | "instant";
export type TourStatus = "completed" | "skipped";
export type TourSlot =
  | "overlay"
  | "spotlight"
  | "card"
  | "media"
  | "title"
  | "content"
  | "progress"
  | "actions"
  | "close"
  | "handle";
export type { Tone as TourTone };

export type TourStep = {
  id: string;
  // a css selector, a function or a ref; none (or not found within 1s) shows the card centered
  target?: TourTarget;
  title: ReactNode;
  content?: ReactNode;
  // an image or a short video above the title
  media?: ReactNode;
  side?: TourSide;
  align?: TourAlign;
  spotlightPadding?: number;
  spotlightRadius?: number;
  // clicks (and typing) reach the highlighted element
  interactive?: boolean;
  // clicking the target goes to the next step (implies interactive)
  advanceOn?: "click";
  // runs before the step shows (open a menu, switch a tab); awaited for at most 3s
  beforeEnter?: () => void | Promise<unknown>;
  nextLabel?: ReactNode;
};

export type TourApi = {
  start: (stepId?: string) => void;
  next: () => void;
  previous: () => void;
  goTo: (step: number | string) => void;
  skip: () => void;
  finish: () => void;
  // forgets the persisted completion, so autoStart runs again
  reset: () => void;
};

export type TourState = TourApi & {
  open: boolean;
  step: number;
  count: number;
  // persisted outcome (with persistKey), null while the tour was never finished
  status: TourStatus | null;
};

export type TourLabels = {
  next: string;
  back: string;
  skip: string;
  done: string;
  close: string;
  progress: (index: number, count: number) => string;
  stepAnnounce: (index: number, count: number, title: string) => string;
  skipped: string;
  completed: string;
};

export const DEFAULT_TOUR_LABELS: TourLabels = {
  next: "Next",
  back: "Back",
  skip: "Skip tour",
  done: "Done",
  close: "Close tour",
  progress: (index, count) => `Step ${index} of ${count}`,
  stepAnnounce: (index, count, title) => `Step ${index} of ${count}: ${title}`,
  skipped: "Tour closed",
  completed: "Tour completed",
};

export type TourProps = {
  steps: TourStep[];
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // the index of the current step
  step?: number;
  defaultStep?: number;
  onStepChange?: (step: number) => void;
  onComplete?: () => void;
  onSkip?: () => void;
  // remembers completion or skip in local storage; autoStart then leaves a finished tour alone
  persistKey?: string;
  // opens after hydration and a 600ms settle, unless persisted as done
  autoStart?: boolean;
  // a global key that starts the tour, e.g. "shift+h"
  shortcut?: string;
  // false: no dimming, the card points at the target with an arrow (coachmarks)
  spotlight?: boolean;
  // dim strength; dark mode adds 0.1
  overlayOpacity?: number;
  spotlightPadding?: number;
  spotlightRadius?: number;
  // a click on the dim area skips
  closeOnOverlayClick?: boolean;
  showProgress?: TourProgressStyle;
  showSkip?: boolean;
  showClose?: boolean;
  // desktop key hints (the arrow on Next)
  keyHints?: boolean;
  scrollBehavior?: TourScrollBehavior;
  // below 768px the card docks at the bottom (default) or stays a popover
  mobileLayout?: TourMobileLayout;
  // docks the card at every width, e.g. inside a phone frame
  docked?: boolean;
  // card width 288 / 340px
  size?: TourSize;
  radius?: TourRadius;
  // the Next button and the progress
  tone?: Tone;
  color?: string;
  // where the overlay renders; default document.body. a transformed element becomes the frame (phone mockups)
  container?: Element | null;
  setApi?: (api: TourApi) => void;
  labels?: Partial<TourLabels>;
  classNames?: Partial<Record<TourSlot, string>>;
  // content that can call useTour()
  children?: ReactNode;
};

// ---------------------------------------------------------------- constants

const CARD_WIDTHS: Record<TourSize, string> = { sm: "w-[288px]", md: "w-[340px]" };
const CARD_PADDING: Record<TourSize, string> = { sm: "p-4", md: "p-5" };
const MEDIA_BLEED: Record<TourSize, string> = { sm: "-mx-4 -mt-4 mb-3.5", md: "-mx-5 -mt-5 mb-4" };
const TITLE_SIZES: Record<TourSize, string> = { sm: "text-[15px]", md: "text-[17px]" };

// W1 SURFACE_RADII; full falls back to lg
const CARD_RADII: Record<TourRadius, string> = {
  none: "rounded-none",
  sm: "rounded-[12px]",
  md: "rounded-2xl",
  lg: "rounded-3xl",
  full: "rounded-3xl",
};

const CARD_SURFACE = "bg-popover text-popover-foreground shadow-modal outline-hidden";

// the docked bar recipe (W11 1.6), split: the wrapper pins and lifts, the card carries the look
const DOCKED_WRAPPER = "fixed inset-x-0 bottom-[var(--bottom-bar-offset,0px)] z-modal";
const DOCKED_CARD =
  "relative rounded-t-[28px] border-t border-foreground/10 bg-popover/95 px-5 pt-3 pb-[max(env(safe-area-inset-bottom),16px)] text-popover-foreground shadow-floating backdrop-blur-xl outline-hidden select-none touch-pan-y";

const HANDLE = "mx-auto mb-3 h-1.5 w-10 shrink-0 rounded-full bg-foreground/15";

// buttons on a photo keep contrast in both themes (the media stage exception)
const MEDIA_CLOSE = "bg-black/45 text-white backdrop-blur-md hover:bg-black/55 [--tone-soft-foreground:white]";

const FOCUSABLE =
  'button:not([disabled]):not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const NO_SWIPE = "input, textarea, select, [contenteditable='true'], [data-no-swipe]";

// ---------------------------------------------------------------- one tour at a time

let lockOwner: string | null = null;
const lockListeners = new Set<() => void>();

function setLock(owner: string | null) {
  lockOwner = owner;
  lockListeners.forEach((notify) => notify());
}

function subscribeLock(notify: () => void) {
  lockListeners.add(notify);
  return () => {
    lockListeners.delete(notify);
  };
}

const subscribeNever = () => () => {};

// ---------------------------------------------------------------- geometry

type Box = { x: number; y: number; width: number; height: number };
type Placement = Exclude<TourSide, "auto">;

function frameBox(container: Element | null | undefined): Box {
  if (container && container !== document.body) {
    const rect = container.getBoundingClientRect();
    return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
  }
  return { x: 0, y: 0, width: document.documentElement.clientWidth || window.innerWidth, height: window.innerHeight };
}

function resolveTarget(target: TourTarget | undefined): Element | null {
  if (!target) return null;
  try {
    if (typeof target === "string") return document.querySelector(target);
    if (typeof target === "function") return target();
    return target.current;
  } catch {
    return null;
  }
}

function isShown(element: Element) {
  return element.isConnected && element.getClientRects().length > 0;
}

function wait(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

// polls for the target (it may render after beforeEnter), up to `timeout` ms
function findTarget(target: TourTarget | undefined, timeout: number, cancelled: () => boolean) {
  return new Promise<Element | null>((resolve) => {
    if (!target) return resolve(null);
    const start = performance.now();
    const tick = () => {
      if (cancelled()) return resolve(null);
      const element = resolveTarget(target);
      if (element && isShown(element)) return resolve(element);
      if (performance.now() - start >= timeout) return resolve(null);
      setTimeout(tick, 50);
    };
    tick();
  });
}

// resolves once the target stops moving for 3 frames (a smooth scroll landed), capped
function settle(element: Element, cancelled: () => boolean, max: number) {
  return new Promise<void>((resolve) => {
    let last = "";
    let same = 0;
    const start = performance.now();
    const tick = () => {
      if (cancelled()) return resolve();
      const rect = element.getBoundingClientRect();
      const key = `${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.width)},${Math.round(rect.height)}`;
      same = key === last ? same + 1 : 0;
      last = key;
      if (same >= 3 || performance.now() - start > max) return resolve();
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

// scrolls the target into view when it is not already comfortably visible. docked phones keep it in the top 55%,
// above the card
function scrollToTarget(element: Element, frame: Box, docked: boolean, behavior: ScrollBehavior) {
  const rect = element.getBoundingClientRect();
  const top = frame.y + 16;
  const bottom = frame.y + (docked ? frame.height * 0.55 : frame.height - 16);
  const fits = rect.top >= top && rect.bottom <= bottom && rect.left >= frame.x && rect.right <= frame.x + frame.width;
  if (fits) return;
  if (!docked || !(element instanceof HTMLElement)) {
    element.scrollIntoView({ block: "center", inline: "nearest", behavior });
    return;
  }
  const previous = element.style.scrollMarginTop;
  element.style.scrollMarginTop = `${Math.max(16, (frame.height * 0.55 - rect.height) / 2)}px`;
  element.scrollIntoView({ block: "start", inline: "nearest", behavior });
  element.style.scrollMarginTop = previous;
}

// "auto": the side with room for the card, below first
function pickSide(element: Element, frame: Box): Placement {
  const rect = element.getBoundingClientRect();
  const space = {
    bottom: frame.y + frame.height - rect.bottom,
    top: rect.top - frame.y,
    right: frame.x + frame.width - rect.right,
    left: rect.left - frame.x,
  };
  if (space.bottom >= 260) return "bottom";
  if (space.top >= 260) return "top";
  if (Math.max(space.right, space.left) >= 380) return space.right >= space.left ? "right" : "left";
  return space.bottom >= space.top ? "bottom" : "top";
}

function textOf(node: ReactNode, fallback: string) {
  return typeof node === "string" || typeof node === "number" ? String(node) : fallback;
}

// keeps Tab inside the card (aria-modal without locking the page)
function trapTab(event: ReactKeyboardEvent<HTMLElement>) {
  if (event.key !== "Tab") return;
  const nodes = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !event.currentTarget.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

// ---------------------------------------------------------------- context

const TourContext = createContext<TourState | null>(null);

// the nearest tour's api and state; null outside a <Tour>
export function useTour(): TourState | null {
  return useContext(TourContext);
}

// ---------------------------------------------------------------- Tour

type Shown = { index: number; target: Element | null; side: Placement };
type HoleValues = { x: MotionValue<number>; y: MotionValue<number>; w: MotionValue<number>; h: MotionValue<number>; r: MotionValue<number>; ring: MotionValue<number> };

export function Tour(tourProps: TourProps) {
  const {
    steps,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    step: stepProp,
    defaultStep = 0,
    onStepChange,
    onComplete,
    onSkip,
    persistKey = "",
    autoStart = false,
    shortcut = "",
    spotlight = true,
    overlayOpacity = 0.55,
    spotlightPadding = 8,
    spotlightRadius = 14,
    closeOnOverlayClick = false,
    showProgress = "dots",
    showSkip = true,
    showClose = true,
    keyHints = true,
    scrollBehavior = "smooth",
    mobileLayout = "docked",
    docked: dockedProp = false,
    size = "md",
    radius = "lg",
    tone = "neutral",
    color,
    container,
    setApi,
    labels: labelsProp,
    classNames,
    children,
  } = useDefaults("tour", tourProps);

  const id = useId();
  const baseId = `tour${id.replace(/[^a-zA-Z0-9]/g, "")}`;
  const labels = useMemo(() => ({ ...DEFAULT_TOUR_LABELS, ...labelsProp }), [labelsProp]);
  const count = steps.length;
  const reduced = usePrefersReducedMotion();
  const isMobile = useIsMobile();
  const isTouch = useIsTouch();
  const docked = dockedProp || (isMobile && mobileLayout === "docked");
  const scale: TourSize = size === "sm" ? "sm" : "md";

  const [open, setOpen] = useControllableState({ value: openProp, defaultValue: defaultOpen, onChange: onOpenChange });
  const [rawStep, setStep] = useControllableState({ value: stepProp, defaultValue: defaultStep, onChange: onStepChange });
  const step = Math.min(Math.max(Math.round(Number(rawStep) || 0), 0), Math.max(count - 1, 0));
  const [status, setStatus, persisted] = usePersistedState<TourStatus | null>(persistKey || null, null);

  // one tour on screen at a time: a second one waits for the lock
  const owner = useSyncExternalStore(subscribeLock, () => lockOwner, () => null);
  const mounted = useSyncExternalStore(subscribeNever, () => true, () => false);
  const active = open && count > 0 && owner === baseId;
  useEffect(() => {
    if (open && count > 0 && lockOwner === null) setLock(baseId);
    if (!open && lockOwner === baseId) setLock(null);
  }, [open, count, owner, baseId]);
  useEffect(() => () => {
    if (lockOwner === baseId) setLock(null);
  }, [baseId]);

  // the step the hole follows (set once its target is found) and the step whose card shows (once scrolled)
  const [spot, setSpot] = useState<Shown | null>(null);
  const [shown, setShown] = useState<Shown | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [direction, setDirection] = useState(1);
  const [prevActive, setPrevActive] = useState(active);
  if (prevActive !== active) {
    setPrevActive(active);
    if (!active) {
      setSpot(null);
      setShown(null);
    }
  }
  const current = active && shown && shown.index === step ? shown : null;

  // the latest values for the stable api and the async step flow
  const latest = useRef({ steps, step, count, open, status, docked, container, reduced, isTouch, scrollBehavior, labels, onComplete, onSkip });
  useInsertionEffect(() => {
    latest.current = { steps, step, count, open, status, docked, container, reduced, isTouch, scrollBehavior, labels, onComplete, onSkip };
  });

  const api = useMemo<TourApi>(() => {
    const tick = () => {
      if (latest.current.isTouch) haptic("selection");
    };
    const indexOf = (target: number | string) => {
      const { steps: list } = latest.current;
      const index = typeof target === "number" ? target : list.findIndex((s) => s.id === target);
      return index >= 0 && index < list.length ? index : -1;
    };
    const close = (outcome: TourStatus) => {
      if (!latest.current.open) return;
      setOpen(false);
      setStatus(outcome);
      setAnnouncement(outcome === "completed" ? latest.current.labels.completed : latest.current.labels.skipped);
      if (outcome === "completed") {
        if (latest.current.isTouch) haptic("success");
        latest.current.onComplete?.();
      } else latest.current.onSkip?.();
    };
    return {
      start: (stepId) => {
        const index = stepId ? indexOf(stepId) : 0;
        setDirection(1);
        setStep(index >= 0 ? index : 0);
        setOpen(true);
      },
      next: () => {
        const { step: index, count: total } = latest.current;
        if (index >= total - 1) return close("completed");
        tick();
        setDirection(1);
        setStep(index + 1);
      },
      previous: () => {
        const { step: index } = latest.current;
        if (index <= 0) return;
        tick();
        setDirection(-1);
        setStep(index - 1);
      },
      goTo: (target) => {
        const index = indexOf(target);
        if (index < 0) return;
        setDirection(index >= latest.current.step ? 1 : -1);
        setStep(index);
        setOpen(true);
      },
      skip: () => close("skipped"),
      finish: () => close("completed"),
      reset: () => {
        persisted.remove();
      },
    };
  }, [setOpen, setStep, setStatus, persisted]);

  useEffect(() => {
    setApi?.(api);
  }, [api, setApi]);

  useShortcut(shortcut || null, () => api.start(), { enabled: Boolean(shortcut) && !open });

  // autoStart once per mount, after hydration and a settle, unless the tour was finished before
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || !persisted.hydrated || autoStarted.current) return;
    autoStarted.current = true;
    if (status) return;
    const timer = setTimeout(() => api.start(), 600);
    return () => clearTimeout(timer);
  }, [autoStart, persisted.hydrated, status, api]);

  // the step flow: beforeEnter, find the target, scroll it into view, glide the hole, then show the card
  const stepId = steps[step]?.id;
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const isCancelled = () => cancelled;
    (async () => {
      const { steps: list, labels: text } = latest.current;
      const def = list[step];
      if (!def) return;
      if (def.beforeEnter) {
        await Promise.race([Promise.resolve().then(def.beforeEnter).catch(() => undefined), wait(3000)]);
        if (cancelled) return;
      }
      const element = await findTarget(def.target, 1000, isCancelled);
      if (cancelled) return;
      const { docked: dock, container: frameEl, reduced: calm, scrollBehavior: behavior } = latest.current;
      const frame = frameBox(frameEl);
      if (element) {
        setSpot({ index: step, target: element, side: "bottom" });
        scrollToTarget(element, frame, dock, calm || behavior === "instant" ? "instant" : "smooth");
        await settle(element, isCancelled, calm || behavior === "instant" ? 120 : 1000);
        if (cancelled) return;
      } else setSpot({ index: step, target: null, side: "bottom" });
      const requested = def.side ?? "auto";
      const side = element ? (requested === "auto" ? pickSide(element, frameBox(frameEl)) : requested) : "bottom";
      setShown({ index: step, target: element && isShown(element) ? element : null, side });
      setAnnouncement(text.stepAnnounce(step + 1, list.length, textOf(def.title, def.id)));
    })();
    return () => {
      cancelled = true;
    };
  }, [active, step, stepId]);

  // ---------- the hole: six motion values written as svg attributes (a documented W11 1.8 deviation)
  const hole: HoleValues = {
    x: useMotionValue(0),
    y: useMotionValue(0),
    w: useMotionValue(0),
    h: useMotionValue(0),
    r: useMotionValue(0),
    ring: useMotionValue(0),
  };
  const holeRef = useRef(hole);
  const holeReady = useRef(false);
  const glideUntil = useRef(0);
  useEffect(() => {
    if (!active) holeReady.current = false;
  }, [active]);

  useEffect(() => {
    if (!active || !spot) return;
    const { x, y, w, h, r, ring } = holeRef.current;
    const def = latest.current.steps[spot.index];
    const pad = def?.spotlightPadding ?? spotlightPadding;
    const round = def?.spotlightRadius ?? spotlightRadius;
    const calm = latest.current.reduced;
    const move = (box: Box, radiusPx: number, ringOpacity: number, glide: boolean) => {
      const pairs: [MotionValue<number>, number][] = [
        [x, box.x],
        [y, box.y],
        [w, Math.max(0, box.width)],
        [h, Math.max(0, box.height)],
        [r, Math.max(0, radiusPx)],
        [ring, ringOpacity],
      ];
      for (const [value, to] of pairs) {
        if (glide) animate(value, to, springs.smooth);
        else {
          value.stop();
          value.set(to);
        }
      }
    };
    const first = !holeReady.current;
    holeReady.current = true;
    if (!first && !calm) glideUntil.current = performance.now() + 650;
    const gliding = () => !calm && performance.now() < glideUntil.current;

    const element = spot.target;
    const frameEl = latest.current.container;
    if (!element) {
      const frame = frameBox(frameEl);
      move({ x: frame.width / 2, y: frame.height / 2, width: 0, height: 0 }, 0, 0, gliding());
      return;
    }

    const lost = () => {
      setSpot((prev) => (prev && prev.index === spot.index ? { ...prev, target: null } : prev));
      setShown((prev) => (prev && prev.index === spot.index ? { ...prev, target: null } : prev));
    };
    const measure = () => {
      if (!isShown(element)) return lost();
      const frame = frameBox(frameEl);
      const rect = element.getBoundingClientRect();
      const box = {
        x: rect.left - frame.x - pad,
        y: rect.top - frame.y - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      };
      move(box, Math.min(round, box.width / 2, box.height / 2), 1, gliding());
    };
    measure();

    // scroll in capture phase so inner scrollers count too; one measure per frame
    let frame = 0;
    const schedule = () => {
      if (!frame)
        frame = requestAnimationFrame(() => {
          frame = 0;
          measure();
        });
    };
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    // a target removed mid-tour falls back to a centered card
    const poll = setInterval(() => {
      if (!isShown(element)) lost();
    }, 400);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      clearInterval(poll);
    };
  }, [active, spot, spotlightPadding, spotlightRadius]);

  // ---------- focus: remembered on open, returned on close
  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;
    return () => {
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [active]);

  // ---------- keys: Escape skips from anywhere; arrows and Enter while focus is in the card
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      if (event.key === "Escape") {
        event.preventDefault();
        api.skip();
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      const inCard = Boolean(target?.closest(`[data-tour-id="${baseId}"]`)) || target === document.body;
      if (!inCard || isTypingTarget(target) || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === "ArrowRight") {
        event.preventDefault();
        api.next();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        api.previous();
      } else if (event.key === "Enter" && !target?.closest("button, a, [role='button']")) {
        event.preventDefault();
        api.next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, api, baseId]);

  // ---------- the highlighted element: described by the card, advanceOn click
  const currentTarget = current?.target ?? null;
  const currentIndex = current?.index ?? -1;
  useEffect(() => {
    if (!currentTarget || currentIndex < 0) return;
    const def = latest.current.steps[currentIndex];
    const cleanups: (() => void)[] = [];
    if (def?.content) {
      const contentId = `${baseId}-content-${currentIndex}`;
      const previous = currentTarget.getAttribute("aria-describedby");
      currentTarget.setAttribute("aria-describedby", previous ? `${previous} ${contentId}` : contentId);
      cleanups.push(() => {
        if (previous === null) currentTarget.removeAttribute("aria-describedby");
        else currentTarget.setAttribute("aria-describedby", previous);
      });
    }
    if (def?.advanceOn === "click") {
      const onClick = () => api.next();
      currentTarget.addEventListener("click", onClick);
      cleanups.push(() => currentTarget.removeEventListener("click", onClick));
    }
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [currentTarget, currentIndex, api, baseId]);

  const state = useMemo<TourState>(() => ({ ...api, open, step, count, status }), [api, open, step, count, status]);

  const shared: CardShared = {
    baseId,
    count,
    size: scale,
    radius,
    tone,
    color,
    showProgress,
    showSkip,
    showClose,
    keyHints,
    labels,
    classNames,
    api,
  };

  const def = current ? steps[current.index] : undefined;
  const interactive = Boolean(current?.target && (def?.interactive || def?.advanceOn === "click"));
  const portal = mounted ? (container ?? document.body) : null;

  return (
    <TourContext.Provider value={state}>
      {children}
      <span data-slot="tour-live" role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
      {portal &&
        createPortal(
          <AnimatePresence>
            {active && (
              <TourLayer key="layer">
                {/* docked coachmarks have no arrow, so the ring alone marks the target */}
                {(spotlight || docked) && (
                  <TourOverlay
                    baseId={baseId}
                    hole={hole}
                    dimmed={spotlight}
                    opacity={overlayOpacity}
                    interactive={interactive}
                    onOverlayClick={closeOnOverlayClick ? api.skip : undefined}
                    classNames={classNames}
                  />
                )}
                {docked ? (
                  shown && <DockedCard shared={shared} shown={shown} steps={steps} direction={direction} />
                ) : (
                  <AnimatePresence mode="wait" propagate>
                    {current && def && current.target ? (
                      <AnchoredCard
                        key={`${current.index}-anchored`}
                        shared={shared}
                        step={def}
                        index={current.index}
                        target={current.target}
                        side={current.side}
                        arrow={!spotlight}
                        pad={def.spotlightPadding ?? spotlightPadding}
                      />
                    ) : current && def ? (
                      <CenteredCard key={`${current.index}-centered`} shared={shared} step={def} index={current.index} />
                    ) : null}
                  </AnimatePresence>
                )}
              </TourLayer>
            )}
          </AnimatePresence>,
          portal,
        )}
    </TourContext.Provider>
  );
}

// ---------------------------------------------------------------- overlay

// keeps the portal mounted until its children finished their exits
function TourLayer({ children }: { children: ReactNode }) {
  return <motion.div exit={{ opacity: 1, transition: { duration: 0.16 } }}>{children}</motion.div>;
}

function TourOverlay({
  baseId,
  hole,
  dimmed,
  opacity,
  interactive,
  onOverlayClick,
  classNames,
}: {
  baseId: string;
  hole: HoleValues;
  // false: the ring only, the page stays usable
  dimmed: boolean;
  opacity: number;
  interactive: boolean;
  onOverlayClick?: () => void;
  classNames?: Partial<Record<TourSlot, string>>;
}) {
  const fade = useMotionPreset("overlay");
  const maskId = `${baseId}-mask`;
  // four blockers around the hole, moved with transforms, let clicks reach an interactive target
  const topY = useTransform(hole.y, (v) => `calc(${v}px - 100%)`);
  const bottomY = useTransform(() => hole.y.get() + hole.h.get());
  const leftX = useTransform(hole.x, (v) => `calc(${v}px - 100%)`);
  const rightX = useTransform(() => hole.x.get() + hole.w.get());
  // springs overshoot a little; svg rejects negative sizes
  const width = useTransform(hole.w, (v) => Math.max(0, v));
  const height = useTransform(hole.h, (v) => Math.max(0, v));
  const round = useTransform(hole.r, (v) => Math.max(0, v));
  const dim = Math.min(Math.max(Number(opacity) || 0, 0), 1);
  const blocker = "pointer-events-auto absolute inset-0";
  const onClick = onOverlayClick;

  return (
    <motion.div
      {...fade}
      data-slot="tour-overlay"
      className={cn("pointer-events-none fixed inset-0 z-overlay", classNames?.overlay)}
      style={{ "--tour-dim": dim } as CSSProperties}
    >
      {dimmed && (
        <svg aria-hidden className="absolute inset-0 size-full">
          <defs>
            <mask id={maskId}>
              <rect width="100%" height="100%" fill="white" />
              <motion.rect x={hole.x} y={hole.y} width={width} height={height} rx={round} fill="black" />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="black"
            mask={`url(#${maskId})`}
            className="[fill-opacity:var(--tour-dim)] dark:[fill-opacity:calc(var(--tour-dim)+0.1)]"
          />
        </svg>
      )}
      {/* the ring follows the same values; one small fixed box, its size written per frame while it glides */}
      <motion.div
        data-slot="tour-spotlight"
        aria-hidden
        className={cn(
          "absolute top-0 left-0 ring-2",
          dimmed
            ? "ring-white/80 shadow-[0_0_0_4px_rgba(255,255,255,0.15)]"
            : "ring-foreground/70 shadow-[0_0_0_6px_color-mix(in_oklab,rgb(var(--wui-foreground))_12%,transparent)]",
          classNames?.spotlight,
        )}
        style={{ x: hole.x, y: hole.y, width, height, borderRadius: round, opacity: hole.ring }}
      />
      {!dimmed ? null : interactive ? (
        <>
          <motion.div data-slot="tour-hit-area" className={blocker} style={{ y: topY }} onClick={onClick} />
          <motion.div data-slot="tour-hit-area" className={blocker} style={{ y: bottomY }} onClick={onClick} />
          <motion.div data-slot="tour-hit-area" className={blocker} style={{ x: leftX }} onClick={onClick} />
          <motion.div data-slot="tour-hit-area" className={blocker} style={{ x: rightX }} onClick={onClick} />
        </>
      ) : (
        <div data-slot="tour-hit-area" className={blocker} onClick={onClick} />
      )}
    </motion.div>
  );
}

// ---------------------------------------------------------------- cards

type CardShared = {
  baseId: string;
  count: number;
  size: TourSize;
  radius: TourRadius;
  tone: Tone;
  color?: string;
  showProgress: TourProgressStyle;
  showSkip: boolean;
  showClose: boolean;
  keyHints: boolean;
  labels: TourLabels;
  classNames?: Partial<Record<TourSlot, string>>;
  api: TourApi;
};

function toneProps(shared: CardShared) {
  return {
    className: toneClasses[shared.tone] ?? toneClasses.neutral,
    style: colorVars(shared.color),
  };
}

// focuses Next (or the target when the step asks for typing) once the card is in place
function useStepFocus(nextRef: RefObject<HTMLButtonElement | null>, step: TourStep | undefined, target: Element | null, key: number) {
  // keyed by the step, not the step object: inline steps arrays must not pull focus back on every parent render
  const interactive = Boolean(step?.interactive || step?.advanceOn === "click");
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      // a step that asks for typing puts the caret in its field (the target or a field inside it)
      const field =
        interactive && target
          ? isTypingTarget(target)
            ? target
            : target.querySelector("input:not([type=hidden]), textarea, [contenteditable='true']")
          : null;
      if (field instanceof HTMLElement && isTypingTarget(field)) field.focus({ preventScroll: true });
      else nextRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [nextRef, interactive, target, key]);
}

function ProgressView({ shared, index, className }: { shared: CardShared; index: number; className?: string }) {
  const { count, showProgress, labels, classNames, api } = shared;
  const snappy = useMotionTransition(springs.snappy);
  if (showProgress === "none" || count < 2) return <span className={cn("sr-only", className)}>{labels.progress(index + 1, count)}</span>;
  const label = labels.progress(index + 1, count);
  if (showProgress === "fraction")
    return (
      <span
        data-slot="tour-progress"
        aria-label={label}
        className={cn("text-[13px] font-medium text-muted-foreground tabular-nums", classNames?.progress, className)}
      >
        {index + 1} / {count}
      </span>
    );
  if (showProgress === "bar")
    return (
      <Progress
        data-slot="tour-progress"
        value={index + 1}
        max={count}
        size="sm"
        tone={shared.tone}
        color={shared.color}
        aria-label={label}
        getValueLabel={() => label}
        className={cn("w-20 min-w-0", classNames?.progress, className)}
      />
    );
  return (
    <div data-slot="tour-progress" className={cn("flex items-center", classNames?.progress, className)}>
      <span className="sr-only">{label}</span>
      {/* the dots are a pointer shortcut; keyboard users page with the arrows */}
      <div aria-hidden className="flex items-center">
        {Array.from({ length: count }, (_, i) => (
          <button
            key={i}
            type="button"
            tabIndex={-1}
            data-state={i === index ? "active" : undefined}
            onClick={() => api.goTo(i)}
            className="grid h-6 w-3.5 cursor-pointer touch-manipulation place-items-center select-none [-webkit-tap-highlight-color:transparent] pointer-coarse:h-11 pointer-coarse:w-7"
          >
            {/* width on a 6px dot: a tiny reflow inside the row, cheaper and steadier than layout while the card moves */}
            <motion.span
              initial={false}
              animate={{ width: i === index ? 16 : 6 }}
              transition={snappy}
              className={cn("block h-1.5 w-1.5 rounded-full", i === index ? "bg-[color:var(--tone)]" : "bg-foreground/20")}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function NextLabel({ last, label, shared, docked }: { last: boolean; label: ReactNode; shared: CardShared; docked: boolean }) {
  const pop = useMotionPreset({
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    transition: springs.bouncy,
  });
  if (last)
    return (
      <>
        {label}
        <motion.span {...pop} aria-hidden className="inline-flex">
          <Check />
        </motion.span>
      </>
    );
  if (docked)
    return (
      <>
        {label}
        <ArrowRight aria-hidden />
      </>
    );
  return (
    <>
      {label}
      {shared.keyHints && (
        <Kbd keys={["→"]} size="sm" variant="ghost" hideOnTouch aria-hidden className="-me-1 text-current opacity-60" />
      )}
    </>
  );
}

function CardBody({
  shared,
  step,
  index,
  target,
  docked = false,
  hasMediaClose,
}: {
  shared: CardShared;
  step: TourStep;
  index: number;
  target: Element | null;
  docked?: boolean;
  hasMediaClose?: boolean;
}) {
  const { baseId, count, size, labels, classNames, api, showSkip, showClose } = shared;
  const nextRef = useRef<HTMLButtonElement>(null);
  useStepFocus(nextRef, step, target, index);
  const last = index >= count - 1;
  const titleId = `${baseId}-title-${index}`;
  const contentId = `${baseId}-content-${index}`;
  const nextText = step.nextLabel ?? (last ? labels.done : labels.next);
  const back = index > 0 && (
    <Button variant="soft" size={docked ? "lg" : "sm"} onClick={api.previous} className={cn(docked && "px-5")}>
      {labels.back}
    </Button>
  );
  const next = (
    <Button
      ref={nextRef}
      tone={shared.tone}
      color={shared.color}
      size={docked ? "lg" : "sm"}
      onClick={api.next}
      className={cn(docked && "flex-1")}
    >
      <NextLabel last={last} label={nextText} shared={shared} docked={docked} />
    </Button>
  );

  return (
    <>
      {step.media && (
        <div
          data-slot="tour-media"
          className={cn(
            "overflow-hidden bg-foreground/5 [&_img]:size-full [&_img]:object-cover [&_video]:size-full [&_video]:object-cover",
            docked ? "mb-4 aspect-[2/1] rounded-2xl" : cn(MEDIA_BLEED[size], "aspect-video rounded-t-[inherit]"),
            classNames?.media,
          )}
        >
          {step.media}
        </div>
      )}
      <h2
        id={titleId}
        data-slot="tour-title"
        className={cn(
          "font-semibold tracking-tight text-balance",
          docked ? "text-[17px]" : TITLE_SIZES[size],
          !docked && showClose && !hasMediaClose && "pe-8",
          classNames?.title,
        )}
      >
        {step.title}
      </h2>
      {step.content && (
        <div
          id={contentId}
          data-slot="tour-content"
          className={cn(
            "mt-1.5 leading-relaxed text-muted-foreground",
            docked ? "text-[15px]" : size === "sm" ? "text-[13px]" : "text-sm",
            classNames?.content,
          )}
        >
          {step.content}
        </div>
      )}
      {docked ? (
        <div data-slot="tour-actions" className={cn("mt-5 flex items-center gap-2", classNames?.actions)}>
          {back}
          {next}
        </div>
      ) : (
        <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-3", size === "sm" ? "mt-3.5" : "mt-4")}>
          <ProgressView shared={shared} index={index} className="me-auto" />
          <div data-slot="tour-actions" className={cn("ms-auto flex items-center gap-1.5", classNames?.actions)}>
            {showSkip && !last && (
              <Button variant="ghost" size="sm" onClick={api.skip} className="px-2.5 text-muted-foreground">
                {labels.skip}
              </Button>
            )}
            {back}
            {next}
          </div>
        </div>
      )}
    </>
  );
}

function CloseButton({ shared, onMedia }: { shared: CardShared; onMedia: boolean }) {
  return (
    <Button
      data-slot="tour-close"
      variant="ghost"
      size="icon-sm"
      radius="full"
      aria-label={shared.labels.close}
      onClick={shared.api.skip}
      className={cn("absolute end-3 top-3 text-muted-foreground", onMedia && MEDIA_CLOSE, shared.classNames?.close)}
    >
      <X />
    </Button>
  );
}

function cardAria(shared: CardShared, step: TourStep, index: number) {
  return {
    role: "dialog",
    "aria-modal": true,
    "aria-labelledby": `${shared.baseId}-title-${index}`,
    "aria-describedby": step.content ? `${shared.baseId}-content-${index}` : undefined,
    "data-tour-id": shared.baseId,
  } as const;
}

function AnchoredCard({
  shared,
  step,
  index,
  target,
  side,
  arrow,
  pad,
}: {
  shared: CardShared;
  step: TourStep;
  index: number;
  target: Element;
  side: Placement;
  arrow: boolean;
  pad: number;
}) {
  const popover = useMotionPreset("popover");
  // floating-ui measures the live target (contextElement lets it watch the target's scrollers)
  const virtualRef = useRef({ getBoundingClientRect: () => target.getBoundingClientRect(), contextElement: target });
  const tones = toneProps(shared);
  const onMedia = Boolean(step.media);
  const prevent = (event: Event) => event.preventDefault();

  return (
    <PopoverPrimitive.Root open>
      <PopoverPrimitive.Anchor virtualRef={virtualRef} />
      <PopoverPrimitive.Content
        asChild
        side={side}
        align={step.align ?? "center"}
        sideOffset={arrow ? 10 : Math.max(0, pad) + 12}
        collisionPadding={12}
        onOpenAutoFocus={prevent}
        onCloseAutoFocus={prevent}
        // radix handles Escape first (and marks it handled), and only for the top-most layer: skip from here
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          shared.api.skip();
        }}
        onInteractOutside={prevent}
      >
        <motion.div
          {...popover}
          {...cardAria(shared, step, index)}
          data-slot="tour-card"
          data-side={side}
          data-size={shared.size}
          onKeyDown={trapTab}
          style={{ transformOrigin: "var(--radix-popover-content-transform-origin)", ...tones.style }}
          className={cn(
            "relative z-modal max-w-[calc(100vw-24px)]",
            CARD_SURFACE,
            CARD_WIDTHS[shared.size],
            CARD_PADDING[shared.size],
            CARD_RADII[shared.radius] ?? CARD_RADII.lg,
            tones.className,
            shared.classNames?.card,
          )}
        >
          {shared.showClose && <CloseButton shared={shared} onMedia={onMedia} />}
          <CardBody shared={shared} step={step} index={index} target={target} hasMediaClose={onMedia} />
          {arrow && (
            <PopoverPrimitive.Arrow width={18} height={9} className="fill-popover drop-shadow-[0_1px_0_color-mix(in_oklab,rgb(var(--wui-foreground))_6%,transparent)]" />
          )}
        </motion.div>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Root>
  );
}

function CenteredCard({ shared, step, index }: { shared: CardShared; step: TourStep; index: number }) {
  const dialog = useMotionPreset("dialog");
  const tones = toneProps(shared);
  const onMedia = Boolean(step.media);
  return (
    <motion.div
      {...dialog}
      {...cardAria(shared, step, index)}
      data-slot="tour-card"
      data-side="center"
      data-size={shared.size}
      onKeyDown={trapTab}
      style={tones.style}
      className={cn(
        "fixed top-1/2 left-1/2 z-modal max-w-[calc(100%-24px)] [translate:-50%_-50%]",
        CARD_SURFACE,
        CARD_WIDTHS[shared.size],
        CARD_PADDING[shared.size],
        CARD_RADII[shared.radius] ?? CARD_RADII.lg,
        tones.className,
        shared.classNames?.card,
      )}
    >
      {shared.showClose && <CloseButton shared={shared} onMedia={onMedia} />}
      <CardBody shared={shared} step={step} index={index} target={null} hasMediaClose={onMedia} />
    </motion.div>
  );
}

// phones: one sheet for the whole tour, content crossfades per step, swipe left / right pages, the keyboard lifts it
function DockedCard({
  shared,
  shown,
  steps,
  direction,
}: {
  shared: CardShared;
  shown: Shown | null;
  steps: TourStep[];
  direction: number;
}) {
  const sheet = useMotionPreset("sheet");
  const reduced = usePrefersReducedMotion();
  const { inset } = useKeyboardInset();
  const lift = useTransform(inset, (v) => -v);
  const drag = useDragControls();
  const x = useMotionValue(0);
  const tones = toneProps(shared);
  const { api, labels, showSkip, showClose, classNames } = shared;

  // the last step that finished resolving stays on screen until the next one is ready
  const index = shown?.index ?? -1;
  const step = index >= 0 ? steps[index] : undefined;
  const last = index >= shared.count - 1;

  const onPointerDown = (event: ReactPointerEvent) => {
    if (event.target instanceof Element && event.target.closest(NO_SWIPE)) return;
    drag.start(event);
  };
  const cardRef = useRef<HTMLDivElement>(null);
  const onDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const width = cardRef.current?.offsetWidth || 360;
    if (info.offset.x < 0 && shouldComplete(-info.offset.x, -info.velocity.x, width)) api.next();
    else if (info.offset.x > 0 && index > 0 && shouldComplete(info.offset.x, info.velocity.x, width)) api.previous();
  };

  const shift = reduced ? 0 : 24 * direction;

  return (
    <motion.div style={{ y: lift }} className={DOCKED_WRAPPER}>
      <motion.div
        {...sheet}
        {...(step ? cardAria(shared, step, index) : { role: "dialog", "aria-modal": true, "data-tour-id": shared.baseId })}
        ref={cardRef}
        data-slot="tour-card"
        data-side="docked"
        layout
        transition={{ ...sheet.transition, layout: reduced ? { duration: 0 } : springs.smooth }}
        drag="x"
        dragListener={false}
        dragControls={drag}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={gestures.elastic}
        dragDirectionLock
        onPointerDown={onPointerDown}
        onDragEnd={onDragEnd}
        onKeyDown={trapTab}
        // radii in style so motion corrects them while the sheet resizes between steps
        style={{ x, borderTopLeftRadius: 28, borderTopRightRadius: 28, ...tones.style }}
        className={cn(DOCKED_CARD, tones.className, classNames?.card)}
      >
        <div data-slot="tour-handle" aria-hidden className={cn(HANDLE, classNames?.handle)} />
        <motion.div layout="position" className="mx-auto w-full max-w-md">
          <div className="mb-3 flex min-h-8 items-center gap-2">
            {index >= 0 && <ProgressView shared={shared} index={index} className="me-auto" />}
            {showSkip && !last ? (
              <Button variant="ghost" size="sm" onClick={api.skip} className="-me-2 ms-auto px-2.5 text-muted-foreground">
                {labels.skip}
              </Button>
            ) : (
              showClose && (
                <Button
                  data-slot="tour-close"
                  variant="ghost"
                  size="icon-sm"
                  radius="full"
                  aria-label={labels.close}
                  onClick={api.skip}
                  className={cn("-me-1.5 ms-auto text-muted-foreground", classNames?.close)}
                >
                  <X />
                </Button>
              )
            )}
          </div>
          <AnimatePresence mode="popLayout" initial={false} custom={shift}>
            {step && (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: shift }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -shift, transition: { duration: 0.12 } }}
                transition={reduced ? { duration: 0.15 } : springs.smooth}
              >
                <CardBody shared={shared} step={step} index={index} target={shown?.target ?? null} docked />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------- TourHotspot

export type TourHotspotPlacement = "top-end" | "top-start" | "bottom-end" | "bottom-start";
export type TourHotspotSlot = "dot" | "ping" | "card" | "title" | "content" | "dismiss";

export type TourHotspotLabels = {
  // the dot's name when the title is not plain text
  open: string;
  dismiss: string;
};

export const DEFAULT_TOUR_HOTSPOT_LABELS: TourHotspotLabels = { open: "Show tip", dismiss: "Got it" };

export type TourHotspotProps = Omit<ComponentProps<"span">, "title" | "content" | "color"> & {
  // wraps children, or anchors to an element elsewhere (selector, function, ref)
  target?: TourTarget;
  title: ReactNode;
  content?: ReactNode;
  side?: Placement;
  align?: TourAlign;
  // the corner the dot sits on
  placement?: TourHotspotPlacement;
  // px; positive moves the dot inward
  offset?: number;
  // remembers that the tip was seen: the dot stays away after it was opened once
  persistKey?: string;
  tone?: Tone;
  color?: string;
  // the ping ring (off under reduced motion)
  pulse?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // after the tip was opened and closed
  onDismiss?: () => void;
  // below 768px the tip opens as a bottom card
  responsive?: boolean;
  labels?: Partial<TourHotspotLabels>;
  classNames?: Partial<Record<TourHotspotSlot, string>>;
};

const HOTSPOT_PLACEMENTS: Record<TourHotspotPlacement, string> = {
  "top-end": "translate-x-1/2 -translate-y-1/2 rtl:-translate-x-1/2",
  "top-start": "-translate-x-1/2 -translate-y-1/2 rtl:translate-x-1/2",
  "bottom-end": "translate-x-1/2 translate-y-1/2 rtl:-translate-x-1/2",
  "bottom-start": "-translate-x-1/2 translate-y-1/2 rtl:translate-x-1/2",
};

// the dot's corner of an element elsewhere on the page, in viewport px, followed through scrolls and resizes
function useCorner(target: TourTarget | undefined, placement: TourHotspotPlacement, offset: number, enabled: boolean) {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const [found, setFound] = useState(false);
  useEffect(() => {
    if (!enabled || !target) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const element = resolveTarget(target);
      const visible = Boolean(element && isShown(element));
      setFound(visible);
      if (!element || !visible) return;
      const rect = element.getBoundingClientRect();
      const rtl = getComputedStyle(element).direction === "rtl";
      const end = placement.endsWith("end") !== rtl;
      x.set(end ? rect.right - offset : rect.left + offset);
      y.set(placement.startsWith("bottom") ? rect.bottom - offset : rect.top + offset);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    schedule();
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    const poll = setInterval(schedule, 500);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      clearInterval(poll);
    };
  }, [enabled, target, placement, offset, x, y]);
  return { x, y, found };
}

export function TourHotspot(tourHotspotProps: TourHotspotProps) {
  const {
    target,
    title,
    content,
    side = "bottom",
    align = "center",
    placement = "top-end",
    offset = 0,
    persistKey = "",
    tone = "neutral",
    color,
    pulse = true,
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    onDismiss,
    responsive = true,
    labels: labelsProp,
    classNames,
    className,
    style,
    children,
    ...props
  } = useDefaults("tour-hotspot", tourHotspotProps);
  const labels = { ...DEFAULT_TOUR_HOTSPOT_LABELS, ...labelsProp };
  const id = useId();
  const reduced = usePrefersReducedMotion();
  const isMobile = useIsMobile();
  const docked = responsive && isMobile;
  const mounted = useSyncExternalStore(subscribeNever, () => true, () => false);
  const where: TourHotspotPlacement = HOTSPOT_PLACEMENTS[placement] ? placement : "top-end";
  const [seen, setSeen] = usePersistedState<boolean>(persistKey || null, false);
  const [open, setOpenState] = useControllableState({ value: openProp, defaultValue: defaultOpen, onChange: onOpenChange });
  const openRef = useRef(open);
  useInsertionEffect(() => {
    openRef.current = open;
  });
  const setOpen = useCallback(
    (next: boolean) => {
      const was = openRef.current;
      setOpenState(next);
      if (was && !next) {
        setSeen(true);
        onDismiss?.();
      }
    },
    [setOpenState, setSeen, onDismiss],
  );

  const detached = !children && Boolean(target);
  const corner = useCorner(target, where, offset, detached && mounted);
  const visible = !seen || open;
  const tones = { className: toneClasses[tone] ?? toneClasses.neutral, style: colorVars(color) };
  const titleId = `${id}-title`;
  const contentId = `${id}-content`;
  const popover = useMotionPreset("popover");
  const pop = useMotionPreset({
    initial: { opacity: 0, scale: 0 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.6, transition: { duration: 0.15 } },
    transition: springs.bouncy,
  });

  const dot = (
    <PopoverPrimitive.Trigger asChild>
      <motion.button
        {...pop}
        type="button"
        data-slot="tour-hotspot"
        data-state={open ? "open" : "closed"}
        aria-label={textOf(title, labels.open)}
        className={cn(
          "pointer-events-auto relative grid size-3 cursor-pointer touch-manipulation place-items-center rounded-full outline-hidden select-none [-webkit-tap-highlight-color:transparent]",
          "after:absolute after:-inset-2 pointer-coarse:after:-inset-4",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline",
          classNames?.dot,
        )}
      >
        {pulse && !reduced && !open && (
          <span
            data-slot="tour-hotspot-ping"
            aria-hidden
            className={cn("absolute inset-0 rounded-full bg-[color:var(--tone)] opacity-70 motion-safe:animate-ping", classNames?.ping)}
          />
        )}
        <span aria-hidden className="relative size-3 rounded-full bg-[color:var(--tone)] ring-2 ring-background shadow-raised" />
      </motion.button>
    </PopoverPrimitive.Trigger>
  );

  const card = (
    <>
      <p id={titleId} data-slot="tour-hotspot-title" className={cn("text-[15px] font-semibold tracking-tight", classNames?.title)}>
        {title}
      </p>
      {content && (
        <div id={contentId} data-slot="tour-hotspot-content" className={cn("mt-1 text-sm leading-relaxed text-muted-foreground", classNames?.content)}>
          {content}
        </div>
      )}
    </>
  );

  const placed = (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.span
          key="dot"
          className={cn(
            "pointer-events-none flex",
            detached ? "fixed top-0 left-0 z-dropdown" : "absolute",
            !detached && (where.startsWith("bottom") ? "bottom-[var(--hotspot-offset)]" : "top-[var(--hotspot-offset)]"),
            !detached && (where.endsWith("start") ? "start-[var(--hotspot-offset)]" : "end-[var(--hotspot-offset)]"),
          )}
          style={detached ? { x: corner.x, y: corner.y, visibility: corner.found ? undefined : "hidden" } : undefined}
          exit={{ opacity: 1, transition: { duration: 0.15 } }}
        >
          <span className={cn("flex", HOTSPOT_PLACEMENTS[where])}>{dot}</span>
        </motion.span>
      )}
    </AnimatePresence>
  );

  return (
    <PopoverPrimitive.Root open={open && !docked} onOpenChange={setOpen}>
      <span
        data-slot="tour-hotspot-anchor"
        className={cn(detached ? "contents" : "relative inline-flex shrink-0", tones.className, className)}
        style={{ "--hotspot-offset": `${offset}px`, ...tones.style, ...style } as CSSProperties}
        {...props}
      >
        {children}
        {detached ? mounted && createPortal(<span className={tones.className} style={tones.style}>{placed}</span>, document.body) : placed}
      </span>
      <AnimatePresence>
        {open && !docked && (
          <PopoverPrimitive.Portal forceMount>
            <PopoverPrimitive.Content
              forceMount
              asChild
              side={side}
              align={align}
              sideOffset={12}
              collisionPadding={12}
              aria-labelledby={titleId}
              aria-describedby={content ? contentId : undefined}
            >
              <motion.div
                {...popover}
                data-slot="tour-hotspot-card"
                style={{ transformOrigin: "var(--radix-popover-content-transform-origin)", ...tones.style }}
                className={cn(
                  "z-popover w-72 max-w-[calc(100vw-24px)] rounded-2xl bg-popover p-4 text-popover-foreground shadow-floating outline-hidden",
                  tones.className,
                  classNames?.card,
                )}
              >
                {card}
                <div className="mt-3 flex justify-end">
                  <Button size="sm" tone={tone} color={color} onClick={() => setOpen(false)} className={classNames?.dismiss}>
                    {labels.dismiss}
                  </Button>
                </div>
                <PopoverPrimitive.Arrow width={18} height={9} className="fill-popover" />
              </motion.div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        )}
      </AnimatePresence>
      {mounted &&
        createPortal(
          <AnimatePresence>
            {open && docked && (
              <HotspotSheet
                key="sheet"
                titleId={titleId}
                contentId={content ? contentId : undefined}
                tones={tones}
                onClose={() => setOpen(false)}
                className={classNames?.card}
                action={
                  <Button size="lg" fullWidth tone={tone} color={color} onClick={() => setOpen(false)} className={classNames?.dismiss}>
                    {labels.dismiss}
                  </Button>
                }
              >
                {card}
              </HotspotSheet>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </PopoverPrimitive.Root>
  );
}

// the tip on phones: a small bottom card over a light scrim; drag down, tap outside or Escape to close
function HotspotSheet({
  titleId,
  contentId,
  tones,
  onClose,
  action,
  className,
  children,
}: {
  titleId: string;
  contentId?: string;
  tones: { className: string; style?: CSSProperties };
  onClose: () => void;
  action: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const overlay = useMotionPreset("overlay");
  const sheet = useMotionPreset("sheet");
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useInsertionEffect(() => {
    closeRef.current = onClose;
  });
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>("button")?.focus({ preventScroll: true }));
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        closeRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKey);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);
  const onDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const height = ref.current?.offsetHeight ?? 240;
    if (shouldComplete(info.offset.y, info.velocity.y, height)) onClose();
  };
  return (
    <>
      <motion.div {...overlay} aria-hidden onClick={onClose} className="fixed inset-0 z-overlay bg-black/40 dark:bg-black/60" />
      <motion.div
        {...sheet}
        ref={ref}
        role="dialog"
        aria-modal
        aria-labelledby={titleId}
        aria-describedby={contentId}
        data-slot="tour-hotspot-card"
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0.05, bottom: 0.6 }}
        onDragEnd={onDragEnd}
        onKeyDown={trapTab}
        style={tones.style}
        className={cn(
          "fixed inset-x-0 bottom-[var(--bottom-bar-offset,0px)] z-modal touch-none rounded-t-[28px] bg-popover px-5 pt-3 pb-[max(env(safe-area-inset-bottom),16px)] text-popover-foreground shadow-modal outline-hidden",
          tones.className,
          className,
        )}
      >
        <div aria-hidden className={HANDLE} />
        <div className="mx-auto w-full max-w-md">
          {children}
          <div className="mt-5">{action}</div>
        </div>
      </motion.div>
    </>
  );
}

